import { Component, OnDestroy, OnInit } from '@angular/core';
import { BaseModal } from 'shared/abstract/base-modal';
import { catchError, EMPTY, Subscription } from 'rxjs';
import { EventBusService } from 'shared/services/event-bus.service';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { emailListPattern } from 'shared/validators/email-list-pattern-validator';
import { ToastService } from 'shared/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { CreateShijiUser } from 'users/interfaces/create-user.interface';
import { UserApiService } from 'shared/services/userApi.service';

@Component({
    selector: 'app-invite-shiji-user',
    templateUrl: './shiji-user-invite.component.html',
    styleUrls: ['./shiji-user-invite.component.scss'],
    standalone: false,
})
export class ShijiUserInviteComponent extends BaseModal implements OnInit, OnDestroy {
    public data: any;
    private subscriptions: { [key: string]: Subscription } = {};
    public newUserForm!: UntypedFormGroup;

    constructor(
        private userApiService: UserApiService,
        private toastService: ToastService,
        private eventBusService: EventBusService,
        private translateService: TranslateService,
    ) {
        super();
        this.newUserForm = new UntypedFormGroup({
            emails: new UntypedFormControl(null, [Validators.required, emailListPattern()]),
            doNotEmail: new UntypedFormControl(null),
        });
    }

    public ngOnInit(): void {
        this.subscriptions.closeModalEvent = this.eventBusService
            .getObservableForEventStream('closeUserModal')
            .subscribe(() => this.closeModalInstance(undefined));
    }

    public ngOnDestroy(): void {
        unsubscribeFromAll(this.subscriptions);
    }

    closeModal() {
        this.eventBusService.dispatch('closeUserModal');
    }

    inviteShijiUser() {
        this.newUserForm.markAllAsTouched();
        this.newUserForm.get('email')?.updateValueAndValidity();

        if (this.newUserForm.valid) {
            const emails = this.newUserForm.value.emails.split(',').filter((email: any) => email !== '');

            const body = {
                emails: emails,
                emailsEnabled: !this.newUserForm.value.doNotEmail,
            } as CreateShijiUser;

            this.subscriptions.createUserInvitation = this.userApiService
                .createShijiUserInvitation(body)
                .pipe(
                    catchError((error) => {
                        if (error.status === 409) {
                            this.toastService.showToast('users.shijiUsers.create.userAlreadyExistsOrInvitationExpired', 'error');
                        }

                        return EMPTY;
                    }),
                )
                .subscribe((response: any) => {
                    if (response.invalidEmails?.length) {
                        this.toastService.showToast(
                            this.translateService.instant('users.shijiUsers.create.alreadyCreated') +
                                response.invalidEmails.join('\n').toString(),
                            'error',
                        );
                    } else {
                        this.toastService.showToast(
                            this.translateService.instant(
                                `users.shijiUsers.create.invite${emails.length > 1 ? 'Multiple' : ''}ShijiUserSuccess`,
                                { user: emails },
                            ),
                            'success',
                        );
                        this.closeModal();
                    }
                });
        }
    }
}
